//CRN_20251218 - ⚠️ ngOnInit() called for components/directives & not services ⚠️

import { catchError, map, Observable, of, switchMap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { eSimApiPaths } from 'src/app/core/consts';
import { ExceptionsService } from '../general/exceptions-service';
import { HttpClientService } from '../general/http-client-service';
import { IdAndDescriptionDTO } from 'src/app/core/interfaces/general/id-description';
import { Injectable } from '@angular/core';
import { IntegerResultDTO } from 'src/app/core/interfaces/general/integer-result';
import { InviewCostCentreDTO } from 'src/app/core/models/inview/inview-cost-centre.dto';
import { EncryptionService } from '../general/encryption-service';
import { StringResultDTO } from 'src/app/core/interfaces/general/string-result';

@Injectable({ providedIn: 'root' })

export class InviewCostCentresService {
  
  constructor(
    private encryptionService: EncryptionService,
    private exceptionsService: ExceptionsService,
    private httpClientService: HttpClientService
  ) { }

  public getByCompanyId(inviewCompanyId: number): Observable<InviewCostCentreDTO[]> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>inviewCompanyId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const inviewCompanyIdEncSS=encResult.result;
                            
                            return this.httpClientService.get<InviewCostCentreDTO[]>(environment.eSimApi +
                                                                                     eSimApiPaths.InviewCostCentres +
                                                                                     "?inviewCompanyIdEncSS=" + inviewCompanyIdEncSS);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getByCompanyId_Lv2(inviewCompanyId: number): Observable<InviewCostCentreDTO[]> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>inviewCompanyId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const inviewCompanyIdEncSS=encResult.result;
                            
                            return this.httpClientService.get<InviewCostCentreDTO[]>(environment.eSimApi +
                                                                                     eSimApiPaths.InviewCostCentres +
                                                                                     "?inviewCompanyIdEncSS=" + inviewCompanyIdEncSS)
                                   .pipe(
                                     map(x => x.filter(x => x.level==2)),
                                     catchError((error) => {
                                       return throwError(() => new Error(this.exceptionsService.formatHttpError(error)))
                                     })
                                   );
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getByCompanyId_Lv1(inviewCompanyId: number, inviewCostCentreId: number): Observable<InviewCostCentreDTO[]> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>inviewCompanyId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const inviewCompanyIdEncSS=encResult.result;
                            
                            return this.httpClientService.get<InviewCostCentreDTO[]>(environment.eSimApi +
                                                                                     eSimApiPaths.InviewCostCentres +
                                                                                     "?inviewCompanyIdEncSS=" + inviewCompanyIdEncSS)
                                   .pipe(
                                     map(x => x.filter(x => x.level==1 && x.parentId==inviewCostCentreId)),
                                     catchError((error) => {
                                       return throwError(() => new Error(this.exceptionsService.formatHttpError(error)))
                                     })
                                   ); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getCostCentreByName(inviewCompanyId: number, costCentreName: string, costCentreLevel: number): Observable<InviewCostCentreDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>inviewCompanyId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const inviewCompanyIdEncSS=encResult.result;
                            
                            return this.httpClientService.get<InviewCostCentreDTO>(environment.eSimApi +
                                                                                   eSimApiPaths.InviewCostCentres + "/" +
                                                                                   eSimApiPaths.InviewGetCostCentreByName +
                                                                                   "?inviewCompanyIdEncSS=" + inviewCompanyIdEncSS +
                                                                                   "&costCentreName=" + costCentreName +
                                                                                   "&costCentreLevel=" + costCentreLevel);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getCostCentreParentId(inviewCompanyId: number, inviewCostCentreId_Lv1: number): Observable<IntegerResultDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>inviewCompanyId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const inviewCompanyIdEncSS=encResult.result;
                            
                            return this.httpClientService.get<IntegerResultDTO>(environment.eSimApi +
                                                                                eSimApiPaths.InviewCostCentres + "/" +
                                                                                eSimApiPaths.InviewCostCentreGetParentID + 
                                                                                "?inviewCompanyIdEncSS=" + inviewCompanyIdEncSS +
                                                                                "&costCentreLv1Id=" + inviewCostCentreId_Lv1);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIdAndDescription_Lv2(inviewCompanyId: number): Observable<IdAndDescriptionDTO[]> {
    try {
      return this.getByCompanyId_Lv2(inviewCompanyId)
      .pipe(
        map(x => x.map(this.idAndDescriptionConverter))
      );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIdAndDescription_Lv1(inviewCompanyId: number, inviewCostCentreId: number): Observable<IdAndDescriptionDTO[]> {
    try {
      return this.getByCompanyId_Lv1(inviewCompanyId, inviewCostCentreId)
      .pipe(
        map(x => x.map(this.idAndDescriptionConverter))
      );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIdAndDescription(inviewCompanyId: number): Observable<IdAndDescriptionDTO[]> {
    try {
      return this.getByCompanyId(inviewCompanyId)
      .pipe(
        map(x => x.map(this.idAndDescriptionConverter))
      );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  private idAndDescriptionConverter(inviewCostCentre: InviewCostCentreDTO) {
    return {id: inviewCostCentre.costCenterId, description: inviewCostCentre.costCenterName};
  }

  public post(inviewCostCentre: InviewCostCentreDTO): Observable<InviewCostCentreDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(JSON.stringify(inviewCostCentre), true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const inviewCostCentreEncSS=encResult.result;
                            
                            return this.httpClientService.post(environment.eSimApi +
                                                               eSimApiPaths.InviewCostCentres +
                                                               "?costCentreEncSS=" + inviewCostCentreEncSS, {});
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
}
